// Browser Geolocation → the coordinates the dashboard request carries (W47.1).
//
// The user picked browser permission over an IP lookup and over a profile
// setting. The reason that is the right default is written down here because
// the fallback is the product, not an error path:
//
//   · **No prompt can equal no feature.** Denied permission, a browser without
//     the API, a non-HTTPS origin, or a timeout all resolve to `null` and the
//     card shows Hong Kong. Nothing in the app breaks and nothing is blocked.
//
//   · **Precision is not asked for.** `enableHighAccuracy: false` with a
//     `timeout` — a weather card needs a city, not a street, and a coarse fix
//     resolves in milliseconds where a precise one can hang.
//
//   · **The answer is not stored.** `sessionStorage` is the whole persistence
//     story, it is per-tab, and it expires on its own. There is no user column
//     and no migration behind this, which is what makes 「不落庫」 true rather
//     than aspirational.

const CACHE_KEY = "heshe:geo";
const CACHE_TTL_MS = 30 * 60 * 1000;
/**
 * Past this the browser's own dialog has usually been auto-dismissed anyway.
 *
 * **8s → 5s (W47.7).** Measured in a real browser: with permission refused the
 * badge sat on 「取得位置中…」 for the full eight seconds and then silently
 * reverted to 「未授權 · 顯示香港」 — eight seconds of a disabled control and no
 * explanation, which is what 「取得位置的卡片失敗」 actually looked like. A coarse
 * fix (`enableHighAccuracy: false`) lands in well under a second on any modern
 * device, so eight seconds was never buying accuracy; it was buying an
 * unexplained pause.
 */
const TIMEOUT_MS = 5000;

/**
 * Why there are no coordinates — `getCurrentPosition` has been handing us this
 * in `error.code` all along and it was being discarded, which is why the card
 * could not tell 「never asked」 from 「asked and refused」 and the badge read
 * the same in both.
 */
export type GeoReason =
  | "unasked"
  | "granted"
  | "denied"
  | "unavailable"
  | "timeout"
  | "unsupported";

export interface GeoResult {
  lat: number | null;
  lon: number | null;
  /** W47.7 — carried through so the card can say what happened. */
  reason: GeoReason;
}

const NO_POSITION = { lat: null, lon: null } as const;

/** A cached fix from earlier in this tab, if it is still fresh. */
function readCache(): GeoResult | null {
  try {
    const raw = window.sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { lat?: unknown; lon?: unknown; at?: unknown };
    const { lat, lon, at } = parsed;
    if (
      typeof lat !== "number" ||
      typeof lon !== "number" ||
      typeof at !== "number" ||
      !Number.isFinite(lat) ||
      !Number.isFinite(lon) ||
      Date.now() - at > CACHE_TTL_MS
    ) {
      return null;
    }
    // Only successes are ever written, so a hit is always a grant.
    return { lat, lon, reason: "granted" };
  } catch {
    // Private-mode Safari throws on sessionStorage access. That is a `null`
    // answer, not a failure — the caller treats both identically.
    return null;
  }
}

function writeCache(lat: number, lon: number): void {
  try {
    window.sessionStorage.setItem(CACHE_KEY, JSON.stringify({ lat, lon, at: Date.now() }));
  } catch {
    // Same: an unwritable store costs one extra permission-free read.
  }
}

function resolvePosition(): Promise<GeoResult> {
  return new Promise((resolve) => {
    // The absence of the API is the most common branch on this screen (older
    // webviews, some in-app browsers). Resolve before asking, never reject.
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      resolve({ ...NO_POSITION, reason: "unsupported" });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lon = position.coords.longitude;
        if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
          resolve({ ...NO_POSITION, reason: "unavailable" });
          return;
        }
        writeCache(lat, lon);
        resolve({ lat, lon, reason: "granted" });
      },
      // 1 PERMISSION_DENIED · 2 POSITION_UNAVAILABLE · 3 TIMEOUT. All three
      // mean 「no coordinates」, and the card treats them the same — but they do
      // not *say* the same thing to the person who just pressed the button, and
      // only one of them is worth retrying.
      (error) => {
        const reason: GeoReason =
          error?.code === 1 ? "denied" : error?.code === 3 ? "timeout" : "unavailable";
        resolve({ ...NO_POSITION, reason });
      },
      { enableHighAccuracy: false, timeout: TIMEOUT_MS, maximumAge: CACHE_TTL_MS },
    );
  });
}

/**
 * Coordinates for the next dashboard request, or `{ null, null }` — which the
 * backend reads as 「fallback 香港」.
 *
 * Never rejects and never throws. Every caller has the same fallback path, so a
 * rejected promise here would only move the failure somewhere less visible.
 */
export async function resolveCoordinates(): Promise<GeoResult> {
  const cached = readCache();
  if (cached) return cached;
  return requestCoordinates();
}

/**
 * Ask the browser **without** reading the cache first.
 *
 * This exists for the re-authorize button on the weather card. `resolveCoordinates`
 * returns a cached answer, and a cached `{null, null}` — written when the first
 * prompt was dismissed — would make the press a no-op that looks like it did
 * something. A person pressing 「重新授權」 is asking the browser again, so the
 * cache has to be out of the way.
 */
export async function requestCoordinates(): Promise<GeoResult> {
  try {
    return await resolvePosition();
  } catch {
    return { ...NO_POSITION, reason: "unavailable" };
  }
}